import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

// Radix Select behind the native `<select>` API. Call sites keep writing
// `<Select value onChange><option/></Select>`, so the ~66 existing usages did not
// have to change when this stopped being a native control. Children are read as
// option descriptors rather than rendered directly.
export type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement>;

type OptionDescriptor = {
  value: string;
  label: string;
  disabled: boolean;
};

function textOf(node: React.ReactNode): string {
  return React.Children.toArray(node)
    .map((child) => {
      if (typeof child === 'string' || typeof child === 'number') return String(child);
      if (React.isValidElement(child)) {
        return textOf((child.props as { children?: React.ReactNode }).children);
      }
      return '';
    })
    .join('');
}

export function readOptions(children: React.ReactNode): OptionDescriptor[] {
  const out: OptionDescriptor[] = [];
  const visit = (node: React.ReactNode): void => {
    React.Children.forEach(node, (child) => {
      if (!React.isValidElement(child)) return;
      if (child.type === React.Fragment) {
        visit((child.props as { children?: React.ReactNode }).children);
        return;
      }
      if (child.type === 'optgroup') {
        visit((child.props as { children?: React.ReactNode }).children);
        return;
      }
      if (child.type !== 'option') return;
      const props = child.props as {
        value?: string | number | readonly string[];
        children?: React.ReactNode;
        disabled?: boolean;
      };
      const value = props.value === undefined ? '' : String(props.value);
      const label = textOf(props.children).trim() || value;
      out.push({ value, label, disabled: props.disabled === true });
    });
  };
  visit(children);
  return out;
}

// Radix treats '' as "no value", so empty-valued placeholder options are mapped
// onto a sentinel and mapped back before any onChange reaches the caller.
const EMPTY = '__empty__';

const nativeValue =
  typeof HTMLSelectElement === 'undefined'
    ? undefined
    : Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value');

// react-hook-form writes defaults, reset() and setValue() straight to `ref.value`
// without firing an event. Intercepting the instance property is the only way the
// visible trigger learns about those writes.
const intercepted = new WeakSet<HTMLSelectElement>();

function interceptValueWrites(node: HTMLSelectElement, onWrite: (next: string) => void): void {
  if (intercepted.has(node)) return;
  intercepted.add(node);
  const base = Object.getOwnPropertyDescriptor(node, 'value') ?? nativeValue;
  if (!base?.get || !base.set) return;
  const get = base.get;
  const set = base.set;
  Object.defineProperty(node, 'value', {
    configurable: true,
    enumerable: base.enumerable,
    get() {
      return get.call(node) as string;
    },
    set(next: unknown) {
      set.call(node, next);
      onWrite(String(next ?? ''));
    },
  });
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      children,
      value,
      defaultValue,
      onChange,
      disabled,
      required,
      name,
      id,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      ...props
    },
    ref,
  ) => {
    const options = React.useMemo(() => readOptions(children), [children]);
    const hidden = React.useRef<HTMLSelectElement | null>(null);

    const [uncontrolled, setUncontrolled] = React.useState(
      defaultValue === undefined ? '' : String(defaultValue),
    );
    const isControlled = value !== undefined;
    const current = isControlled ? String(value) : uncontrolled;

    // react-hook-form hands out a new ref callback on every render; keeping
    // this callback stable stops React from detaching and re-attaching the
    // hidden select (and re-wrapping its value setter) on each keystroke.
    const forwardedRef = React.useRef(ref);
    forwardedRef.current = ref;
    const attachRef = React.useCallback((node: HTMLSelectElement | null) => {
      hidden.current = node;
      if (node) interceptValueWrites(node, setUncontrolled);
      const target = forwardedRef.current;
      if (typeof target === 'function') target(node);
      else if (target) target.current = node;
    }, []);

    // The hidden select is uncontrolled so external writes are not fought by
    // React; this keeps its DOM value aligned with what the trigger shows,
    // including after the option list arrives asynchronously.
    React.useLayoutEffect(() => {
      const node = hidden.current;
      if (node && node.value !== current) nativeValue?.set?.call(node, current);
    }, [current, options]);

    const placeholder = options.find((o) => o.value === '')?.label ?? 'Select…';

    const emit = (next: string): void => {
      if (!isControlled) setUncontrolled(next);
      const node = hidden.current;
      if (!node) return;
      // Drive the hidden native select through its own value setter so React's
      // synthetic onChange sees a real change event with the right target.
      nativeValue?.set?.call(node, next);
      node.dispatchEvent(new Event('change', { bubbles: true }));
    };

    return (
      <>
        <select
          ref={attachRef}
          className="sr-only"
          tabIndex={-1}
          aria-hidden
          name={name}
          required={required}
          disabled={disabled}
          defaultValue={current}
          onChange={onChange}
          {...props}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <SelectPrimitive.Root
          value={current === '' ? EMPTY : current}
          onValueChange={(v) => {
            // Radix also fires this from its form-autofill bubble select, which
            // reports '' when the value has no matching option yet (options still
            // loading). Items never carry '', so that path would wipe a
            // reset()-written value before its option arrives.
            if (v === '') return;
            const next = v === EMPTY ? '' : v;
            if (!options.some((o) => o.value === next)) return;
            emit(next);
          }}
          disabled={disabled}
        >
          <SelectPrimitive.Trigger
            id={id}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            className={cn(
              'flex h-11 w-full items-center justify-between gap-2 rounded-xl border border-[hsl(var(--input))] bg-[hsl(var(--background))] px-4 py-2 text-left text-sm text-[hsl(var(--foreground))] transition-colors',
              'hover:border-[hsl(var(--brand))]/35',
              'focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-1 focus-visible:ring-offset-[hsl(var(--background))]',
              'data-[state=open]:border-[hsl(var(--brand))] data-[state=open]:ring-2 data-[state=open]:ring-[hsl(var(--ring))]',
              'disabled:cursor-not-allowed disabled:opacity-50',
              '[&>span]:truncate data-[placeholder]:text-[hsl(var(--muted-foreground))]',
              className,
            )}
          >
            <SelectPrimitive.Value placeholder={placeholder} />
            <SelectPrimitive.Icon asChild>
              <ChevronDown className="h-4 w-4 shrink-0 text-[hsl(var(--muted-foreground))] transition-transform duration-200 data-[state=open]:rotate-180" />
            </SelectPrimitive.Icon>
          </SelectPrimitive.Trigger>

          <SelectPrimitive.Portal>
            <SelectPrimitive.Content
              position="popper"
              sideOffset={6}
              className={cn(
                'relative z-50 max-h-72 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--popover))] text-[hsl(var(--popover-foreground))] shadow-lg shadow-black/5',
                'origin-[var(--radix-select-content-transform-origin)] animate-[select-in_130ms_ease-out]',
              )}
            >
              <SelectPrimitive.Viewport className="p-1">
                {options.map((o) => (
                  <SelectPrimitive.Item
                    key={o.value}
                    value={o.value === '' ? EMPTY : o.value}
                    disabled={o.disabled}
                    className={cn(
                      'relative flex cursor-pointer select-none items-center rounded-lg py-2 pl-3 pr-8 text-sm outline-none transition-colors',
                      'focus:bg-[hsl(var(--brand))]/10 focus:text-[hsl(var(--foreground))]',
                      'data-[state=checked]:font-medium data-[state=checked]:text-[hsl(var(--brand))]',
                      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                    )}
                  >
                    <SelectPrimitive.ItemText>{o.label}</SelectPrimitive.ItemText>
                    <span className="absolute right-2.5 flex h-4 w-4 items-center justify-center">
                      <SelectPrimitive.ItemIndicator>
                        <Check className="h-4 w-4" />
                      </SelectPrimitive.ItemIndicator>
                    </span>
                  </SelectPrimitive.Item>
                ))}
              </SelectPrimitive.Viewport>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>
      </>
    );
  },
);
Select.displayName = 'Select';
